/**
 * Pure decision logic for the POS offline sale queue. No browser APIs, no
 * IndexedDB, no axios — everything here is a plain function of its arguments so
 * it can be unit-tested directly (server/tests/posOfflineQueue.test.js) without
 * a DOM or a running server.
 *
 * WHY THIS FILE IS CommonJS
 * -------------------------
 * The client is an ESM/webpack bundle, but the test runner is `node --test`
 * against server/, and client/package.json is not `"type": "module"`. Writing
 * the policy as CommonJS lets Node require() it as-is while webpack still
 * imports it normally (default import from CJS is fully supported). The
 * alternative — duplicating these rules in a test — is exactly how the original
 * bug survived: the rule was never asserted anywhere.
 */

// Statuses that mean "the sale is fine, the attempt wasn't".
//
// This set is the whole point of the module. The original implementation
// dropped a queued sale on ANY 4xx, which meant the single most likely response
// to the first flush after a long outage — a 401, because the JWT is 24h and
// there is no refresh endpoint — silently destroyed a sale the customer had
// already paid for and walked out with. A rejected *attempt* is not a rejected
// *sale*.
const RETRYABLE_STATUSES = new Set([
  401, // token expired mid-outage; resolves once the cashier signs back in
  403, // permission re-evaluated server-side; may be transient (licence, role reload)
  408, // request timeout
  423, // locked
  425, // too early
  429, // rate limited — the reconnecting-fleet case
]);

// After this many failed attempts a queued sale stops being retried silently and
// is surfaced to the operator instead. It is NEVER discarded: an unsyncable sale
// is a reconciliation problem for a human, not something for the till to bin.
const MAX_SYNC_ATTEMPTS = 20;

/**
 * Decide what to do with a queued sale whose flush attempt failed.
 *
 * @param {any} err       the error thrown by the poster (axios-shaped)
 * @param {number} attempts how many times this entry has now been tried
 * @returns {'retry'|'drop'|'needs_attention'}
 *   retry           — keep queued, try again on the next flush
 *   drop            — the server evaluated the sale and rejected it on its
 *                     merits; remove it and surface the reason
 *   needs_attention — keep queued, but stop retrying quietly and tell someone
 */
function classifySyncFailure(err, attempts = 0) {
  const status = err && err.response ? err.response.status : null;

  // No response at all → the network is still down. Always retry; this is the
  // ordinary offline path and must never consume the attempt budget's meaning.
  if (!status) {
    return attempts >= MAX_SYNC_ATTEMPTS ? 'needs_attention' : 'retry';
  }

  if (RETRYABLE_STATUSES.has(status)) {
    return attempts >= MAX_SYNC_ATTEMPTS ? 'needs_attention' : 'retry';
  }

  // 5xx is the server failing, not the sale failing.
  if (status >= 500) {
    return attempts >= MAX_SYNC_ATTEMPTS ? 'needs_attention' : 'retry';
  }

  // A genuine business rejection: validation, closed session, insufficient
  // stock, duplicate. The server looked at this sale and said no. Dropping it
  // is correct — otherwise one bad sale wedges the queue forever — but the
  // caller must surface the reason so a human can reconcile it.
  if (status >= 400) return 'drop';

  return 'retry';
}

/**
 * IndexedDB database name for a tenant.
 *
 * The original code used a single hardcoded 'journey_pos' for every tenant. On a
 * shared browser that let one tenant's operator see — and flush — another
 * tenant's queued sales, which is a cross-tenant data leak in a product whose
 * entire isolation model is database-per-tenant.
 */
function dbNameForTenant(tenantDb) {
  const suffix = String(tenantDb || '').trim() || 'default';
  return `journey_pos__${suffix}`;
}

/** The pre-namespacing database name, kept so its contents can be rescued once. */
const LEGACY_DB_NAME = 'journey_pos';

function decodeBase64Url(input) {
  const b64 = String(input).replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  if (typeof atob === 'function') return atob(padded);
  return Buffer.from(padded, 'base64').toString('binary');
}

/**
 * Read the `tenantDb` claim out of a JWT without verifying it.
 *
 * Verification is deliberately skipped: this value is only ever used as a local
 * cache key. It never grants access to anything — the server re-derives the
 * tenant from the same claim on every request and routes the query itself
 * (server/middleware/auth.js). A forged token here would only let an attacker
 * partition their own browser storage differently.
 */
function tenantFromToken(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    return payload && payload.tenantDb ? String(payload.tenantDb) : null;
  } catch {
    return null;
  }
}

module.exports = {
  RETRYABLE_STATUSES,
  MAX_SYNC_ATTEMPTS,
  LEGACY_DB_NAME,
  classifySyncFailure,
  dbNameForTenant,
  tenantFromToken,
};
